/**
 * project actions.
 */
import { createAction } from '@reduxjs/toolkit';
import { CaseId, TDOId } from '@common-modules/universal/models/Brands';

import { ProjectKind, ProjectPointer } from './models';
import type { CombinedAssetSummary } from './combineServices';
import type { ProjectBatesStamp } from './batesStamp';
import type { ProjectRedactionData } from './projectRedactionAsset';
import type {
  AppliedWatermark,
  BatesStampSettings,
} from '@redact-modules/documents/models';
import type { MergedFilenameOptions } from './models';

/**
 * Set up a merged document for a request.
 */
export const CREATE_PROJECT_REQUEST = createAction<{
  readonly caseId: CaseId;
  readonly requestName: string;
  readonly kind: ProjectKind;
  readonly members: readonly TDOId[];
}>('redact/projects/CREATE_PROJECT_REQUEST');

/**
 * Carries the request's FULL pointer list, not just the new entry, so the
 * reducer writes what the server now holds rather than appending to a local
 * copy that may have drifted.
 */
export const CREATE_PROJECT_SUCCESS = createAction<{
  readonly caseId: CaseId;
  readonly projectTdoId: TDOId;
  readonly pointers: readonly ProjectPointer[];
}>('redact/projects/CREATE_PROJECT_SUCCESS');

export const CREATE_PROJECT_FAILURE = createAction<{
  readonly caseId: CaseId;
}>('redact/projects/CREATE_PROJECT_FAILURE');

/**
 * Open a request's merged document in the editor.
 */
export const OPEN_PROJECT_REQUEST = createAction<{
  readonly caseId: CaseId;
  readonly projectTdoId: TDOId;
}>('redact/projects/OPEN_PROJECT_REQUEST');

/**
 * Read a project's membership.
 */
export const LOAD_PROJECT_REQUEST = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/LOAD_PROJECT_REQUEST');

export const LOAD_PROJECT_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  readonly members: readonly TDOId[];
  /** The project record's own stamp — when this membership was decided. */
  readonly modifiedDateTime?: string;
}>('redact/projects/LOAD_PROJECT_SUCCESS');

export const LOAD_PROJECT_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/LOAD_PROJECT_FAILURE');

/**
 * Change which documents a request's merged document includes.
 */
export const UPDATE_PROJECT_REQUEST = createAction<{
  readonly projectTdoId: TDOId;
  readonly members: readonly TDOId[];
  /**
   * The membership the caller BUILT THIS FROM, if it had one.
   *
   * Carried so the write can refuse a list composed against a set that has
   * since moved — checked in `updateProjectMembers`, which re-reads the
   * record anyway and so holds the only copy that is actually authoritative.
   * A caller with no expectation (the editor's panel, which composes from
   * live state each time) omits it and is written as before.
   */
  readonly expectedMembers?: readonly TDOId[];
}>('redact/projects/UPDATE_PROJECT_REQUEST');

export const UPDATE_PROJECT_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  readonly members: readonly TDOId[];
  /**
   * The stamp this write PUT ON THE RECORD, carried so the store holds the
   * same string rather than a second reading of the clock.
   */
  readonly decidedAt?: string;
}>('redact/projects/UPDATE_PROJECT_SUCCESS');

export const UPDATE_PROJECT_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/UPDATE_PROJECT_FAILURE');

export const DELETE_PROJECT_REQUEST = createAction<{
  readonly caseId: CaseId;
  readonly projectTdoId: TDOId;
}>('redact/projects/DELETE_PROJECT_REQUEST');

/** Carries the pointer list as it stands AFTER the removal. */
export const DELETE_PROJECT_SUCCESS = createAction<{
  readonly caseId: CaseId;
  readonly projectTdoId: TDOId;
  readonly pointers: readonly ProjectPointer[];
}>('redact/projects/DELETE_PROJECT_SUCCESS');

export const DELETE_PROJECT_FAILURE = createAction<{
  readonly caseId: CaseId;
}>('redact/projects/DELETE_PROJECT_FAILURE');

/**
 * What it takes to build the request's merged document.
 *
 * Named so a caller that FORWARDS a combine rather than issuing one can hold
 * it without restating the shape — Redact All runs the burn pass first and
 * then rebuilds, and the merge it rebuilds has to be the same merge the
 * Merged PDF slot's own Update would have produced (VE-31006).
 */
export interface CombineProjectRequestPayload {
  readonly projectTdoId: TDOId;
  /** Names the output file; see `buildMergedDocumentFilename`. */
  readonly requestName: string;
  /** The members to combine, in page order. */
  readonly members: readonly TDOId[];
  /**
   * Members the combine may LEAVE OUT rather than refuse over: files nobody
   * has opened, which have no saved redaction data at all. The caller decides,
   * because it is the side that can see that; the saga only has ids.
   */
  excludable?: ReadonlyArray<{ tdoId: TDOId; name: string }>;
}

export const COMBINE_PROJECT_REQUEST =
  createAction<CombineProjectRequestPayload>(
    'redact/projects/COMBINE_PROJECT_REQUEST'
  );

/**
 * The version number this rebuild was issued, the moment the server settles it.
 *
 * THE NUMBER THE BUILD GOT, not one the UI derives. The processing banner was
 * computing "one past the newest combined asset", which is right until the
 * assets are gone: delete the merged PDF and rebuild, and the banner announced
 * version 1 over a build the server had correctly numbered 5. `latestCombined
 * Version` on the project TDO is what keeps the count honest across a delete,
 * and it is read server-side inside `serviceNextCombinedVersion` — it never
 * reaches the store, so no amount of arithmetic on this side could have found
 * it.
 */
export const COMBINE_PROJECT_VERSION = createAction<{
  readonly projectTdoId: TDOId;
  readonly version: number;
}>('redact/projects/COMBINE_PROJECT_VERSION');

export const COMBINE_PROJECT_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  readonly assetId: string;
  readonly version: number;
  readonly filename: string;
}>('redact/projects/COMBINE_PROJECT_SUCCESS');

export const COMBINE_PROJECT_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/COMBINE_PROJECT_FAILURE');

/**
 * Which project's combined document is being PREVIEWED, or null for none.
 */
export const SET_COMBINED_PREVIEW = createAction<{
  projectTdoId: TDOId | null;
}>('redact/projects/SET_COMBINED_PREVIEW');

/**
 * Record that the combined document went out.
 */
export const INCREMENT_COMBINED_DOWNLOAD = createAction<{
  projectTdoId: TDOId;
}>('redact/projects/INCREMENT_COMBINED_DOWNLOAD');

/**
 * Rename the merged document, or reset it to the request's name.
 */
export const RENAME_PROJECT_REQUEST = createAction<{
  projectTdoId: TDOId;
  name: string;
  /**
   * The filename choices, saved WITH the name because they are made on the
   * same screen and describe the same thing.
   */
  filenameOptions: MergedFilenameOptions;
}>('redact/projects/RENAME_PROJECT_REQUEST');

/**
 * The rename landed. Carries the name the caller asked for, because that is
 * the authoritative one: the server's own reply can still echo the old name
 * from a cache the write does not clear (see `serviceRenameProjectTdo`).
 */
export const RENAME_PROJECT_SUCCESS = createAction<{
  projectTdoId: TDOId;
  name: string;
  filenameOptions: MergedFilenameOptions;
}>('redact/projects/RENAME_PROJECT_SUCCESS');

/**
 * The rename did not land, and the optimistic write has to go back.
 */
export const RENAME_PROJECT_FAILURE = createAction<{
  projectTdoId: TDOId;
}>('redact/projects/RENAME_PROJECT_FAILURE');

/**
 * Delete a project's combined document asset.
 */
export const DELETE_COMBINED_REQUEST = createAction<{
  projectTdoId: TDOId;
  assetId: string;
}>('redact/projects/DELETE_COMBINED_REQUEST');

export const DELETE_COMBINED_SUCCESS = createAction<{
  projectTdoId: TDOId;
}>('redact/projects/DELETE_COMBINED_SUCCESS');

export const DELETE_COMBINED_FAILURE = createAction<{
  projectTdoId: TDOId;
  message: string;
}>('redact/projects/DELETE_COMBINED_FAILURE');

/**
 * Read the project's newest merged document, so the editor can show it.
 */
export const LOAD_COMBINED_REQUEST = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/LOAD_COMBINED_REQUEST');

export const LOAD_COMBINED_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  /** Null when the project has never been merged. */
  readonly asset: CombinedAssetSummary | null;
  /**
   * The project TDO's own name — the merged document's display name, which a
   * reviewer can change. Rides along because the lookup reads the TDO anyway,
   * and because it is wanted even when no merged document exists yet.
   */
  projectName: string | null;
  /** Read in the same lookup, for the same reason. */
  readonly filenameOptions?: MergedFilenameOptions;
  /**
   * The project's Bates settings, read in the same lookup. Null means the
   * project has none — distinct from the key being absent, which means the
   * lookup has not run.
   */
  readonly batesStamp?: ProjectBatesStamp | null;
}>('redact/projects/LOAD_COMBINED_SUCCESS');

export const LOAD_COMBINED_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/LOAD_COMBINED_FAILURE');

// --- per-call outcomes, consumed by the saga only ---------------------------

export const CREATE_PROJECT_TDO_INIT =
  'redact/projects/CREATE_PROJECT_TDO_INIT';

export const CREATE_PROJECT_TDO_SUCCESS = createAction<{
  readonly createTDO: { readonly id: TDOId; readonly name: string } | null;
}>('redact/projects/CREATE_PROJECT_TDO_SUCCESS');

export const CREATE_PROJECT_TDO_FAILURE = createAction<unknown>(
  'redact/projects/CREATE_PROJECT_TDO_FAILURE'
);

/**
 * These two carry the record they are about, and the sagas match on it.
 */
export const WRITE_PROJECT_POINTERS_SUCCESS = createAction<{
  readonly caseSdoId: string;
}>('redact/projects/WRITE_PROJECT_POINTERS_SUCCESS');

export const WRITE_PROJECT_POINTERS_FAILURE = createAction<{
  readonly caseSdoId: string;
  readonly reason?: string;
}>('redact/projects/WRITE_PROJECT_POINTERS_FAILURE');

/** Correlated by project, for the same reason the pointer outcomes are. */
export const UPDATE_PROJECT_TDO_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  /** See `UPDATE_PROJECT_SUCCESS.decidedAt`. */
  readonly decidedAt?: string;
  /**
   * The membership the write REPLACED, read from the record moments before
   * the write — the only authoritative copy there is (VE-31977).
   *
   * NOT A CLIENT CACHE, for the same reason `expectedMembers` is checked
   * against the re-read rather than against `membersByProject`: another tab
   * or another reviewer can have moved the set, and an audit entry that
   * diffs against a stale local copy reports a change nobody made.
   */
  readonly previousMembers?: readonly TDOId[];
}>('redact/projects/UPDATE_PROJECT_TDO_SUCCESS');

export const UPDATE_PROJECT_TDO_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
  readonly reason?: string;
}>('redact/projects/UPDATE_PROJECT_TDO_FAILURE');

export const DELETE_PROJECT_TDO_INIT =
  'redact/projects/DELETE_PROJECT_TDO_INIT';

export const DELETE_PROJECT_TDO_SUCCESS = createAction<unknown>(
  'redact/projects/DELETE_PROJECT_TDO_SUCCESS'
);

export const DELETE_PROJECT_TDO_FAILURE = createAction<unknown>(
  'redact/projects/DELETE_PROJECT_TDO_FAILURE'
);

/**
 * Apply Bates settings to a merged document — every member at once
 * (VE-30105). The saga writes `details.batesStamp` on the project TDO; no
 * member is touched.
 */
export const SET_PROJECT_BATES_REQUEST = createAction<{
  readonly projectTdoId: TDOId;
  readonly settings: BatesStampSettings;
}>('redact/projects/SET_PROJECT_BATES_REQUEST');

export const SET_PROJECT_BATES_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  readonly batesStamp: ProjectBatesStamp;
}>('redact/projects/SET_PROJECT_BATES_SUCCESS');

export const SET_PROJECT_BATES_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/SET_PROJECT_BATES_FAILURE');

/**
 * Switch a merged document's Bates numbering OFF (VE-30105).
 *
 * Its own action rather than a SET with empty settings: "no stamping" and
 * "stamping configured to draw nothing" are different states, and only the
 * first should leave members unnumbered. Members go stale, because their
 * outputs still carry numbers the set no longer has.
 */
/**
 * Read the merged document's `project-redaction-data` asset (VE-30104).
 *
 * ITS OWN ACTION, not a field on LOAD_COMBINED. The Bates record rides on
 * LOAD_COMBINED_SUCCESS and nothing writes it on LOAD_COMBINED_FAILURE, so one
 * failed merged-document lookup leaves it unread for the rest of the session
 * while every reader reports the set current. This one records its own
 * failure, which is why `ProjectRedactionRecord` has a `failed` state.
 */
/**
 * Store the merged document's watermarks (VE-30104).
 *
 * ONE ACTION FOR EVERY STATE, where Bates needed a SET and a CLEAR. There,
 * "no stamping" and "stamping configured to draw nothing" are different
 * states that look alike. Here the body says both outright: an empty
 * `watermarks` is none, and `watermarksOff` is configured-but-hidden. Nothing
 * is ambiguous, so nothing needs a second action to disambiguate it.
 */
/**
 * EVERY WRITE CARRIES ITS OWN ID, minted here so no dispatcher has to
 * remember to, and echoed back by the success and the failure.
 *
 * Writes to this record serialise rather than drop, so two can be in play at
 * once: one in flight, the next optimistically applied and queued. Without an
 * id the two results are indistinguishable, and the earlier one spoke for the
 * later — its SUCCESS overwrote the queued write's record and deleted its
 * rollback slot, so the second apply was lost locally and on the server under
 * a panel that said "Updated", and a failure after that rolled nothing back
 * and recorded no failure at all.
 *
 * The id says which write a result is about. A result for a write that has
 * since been superseded updates what we would roll back TO, and otherwise
 * leaves the newer write's record alone.
 *
 * A COUNTER rather than a uuid, and do not swap it for one: `uuid.v4` is
 * mocked to a constant repo-wide (`test/setupTests.js`), so every write would
 * share an id and no test could tell them apart. It never leaves the tab
 * either — nothing persists it and no server sees it — so randomness buys
 * nothing here.
 */
let nextWatermarkWriteId = 0;

export const SET_PROJECT_WATERMARKS_REQUEST = createAction(
  'redact/projects/SET_PROJECT_WATERMARKS_REQUEST',
  (payload: {
    readonly projectTdoId: TDOId;
    readonly watermarks: readonly AppliedWatermark[];
    readonly watermarksOff: boolean;
  }) => {
    nextWatermarkWriteId += 1;
    return { payload: { ...payload, writeId: `w${nextWatermarkWriteId}` } };
  }
);

export const SET_PROJECT_WATERMARKS_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  readonly writeId: string;
  readonly data: ProjectRedactionData;
}>('redact/projects/SET_PROJECT_WATERMARKS_SUCCESS');

export const SET_PROJECT_WATERMARKS_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
  readonly writeId: string;
}>('redact/projects/SET_PROJECT_WATERMARKS_FAILURE');

export const LOAD_PROJECT_REDACTION_REQUEST = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/LOAD_PROJECT_REDACTION_REQUEST');

export const LOAD_PROJECT_REDACTION_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
  /** Null when the project carries no record — read, and empty. */
  readonly data: ProjectRedactionData | null;
  /** Image marks whose artwork this read could not restore. */
  readonly artworkFailures?: readonly string[];
}>('redact/projects/LOAD_PROJECT_REDACTION_SUCCESS');

export const LOAD_PROJECT_REDACTION_FAILURE = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/LOAD_PROJECT_REDACTION_FAILURE');

export const CLEAR_PROJECT_BATES_REQUEST = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/CLEAR_PROJECT_BATES_REQUEST');

/**
 * The clear LANDED on the server.
 *
 * Its own action, where the first cut just let the request stand: the audit
 * event hung off the request, so a clear that threw — rolled back in the UI,
 * error snackbar and all — still recorded "Removed Bates stamping" against a
 * release whose numbering was never removed. It also leaves the rollback slot
 * behind, which is what the store reads as "a write is in flight".
 */
export const CLEAR_PROJECT_BATES_SUCCESS = createAction<{
  readonly projectTdoId: TDOId;
}>('redact/projects/CLEAR_PROJECT_BATES_SUCCESS');
